import { useState } from 'react';

import { Button } from '@components/button';
import { CopyButton } from '@components/copy-button';
import { MaxLengthCounter } from '@components/max-length-counter';
import { hideModal, Modal } from '@components/modal';
import { showModal } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

export const showColumnDetailModal = (props: ColumnDetailModalProps) => {
  showModal({ component: ColumnDetailModal, props, options: { variant: 'LARGE' } });
};

export type ColumnDetailModalProps = {
  onSave?: (value: string) => void;
  defaultValue: string;
  maxLength?: number;
};

const ColumnDetailModal = ({
  onSave,
  defaultValue,
  maxLength = Infinity,
}: ColumnDetailModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [value, setValue] = useState(defaultValue);
  const [isSaving, setIsSaving] = useState(false);
  const isEditable = Boolean(onSave);

  const handleSave = async () => {
    setIsSaving(true);
    await onSave?.(value);
    setIsSaving(false);
    hideModal();
  };

  return (
    <Modal>
      <Modal.Content>
        <TextArea
          value={value}
          onChange={setValue}
          style={{
            minHeight: maxLength < 256 ? pxToRem(112) : '50vh',
            width: '100%',
            resize: 'none',
          }}
          isDisabled={!isEditable}
          autoFocus={false}
          maxLength={maxLength}
        />
        {isEditable && maxLength !== Infinity && (
          <MaxLengthCounter currentLength={value.length} maxLength={maxLength} />
        )}
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="space-between">
          <CopyButton
            getText={() => value}
            hasToast
            size="sm"
            onClick={hideModal}
            copyLabel={t('copyAndClose')}
          />
          <Stack orientation="horizontal" space={2} align="center">
            {!isEditable && <Text text={t('notEditable')} color="textHelper" font="regular14" />}
            <Button
              text={t('core.close')}
              variant="secondary"
              onClick={hideModal}
              size="sm"
              autoFocus
            />
            {isEditable && (
              <Button
                text={t('core.save')}
                size="sm"
                disabled={isSaving || value === defaultValue}
                variant="primary-blue"
                onClick={handleSave}
              />
            )}
          </Stack>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    copyAndClose: 'Copy & close',
    notEditable: 'Not editable',
  },
};
